import React, { useEffect, useMemo, useState } from "react";
import { endpoints } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Copy, Sparkles, Check, ArrowUpRight, Flower2, ImageUp, Send, Heart, Zap, History, ShieldCheck, ShieldOff, ShieldAlert, Clock } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const TIME_OPTIONS = [
  { key: "morning", label: "Morning" },
  { key: "anytime", label: "Anytime" },
  { key: "evening", label: "Evening" },
];

const PROFESSIONAL_TYPES = [
  { key: "spa", label: "Spa" },
  { key: "personal_trainer", label: "Personal Trainer" },
  { key: "fitness_movement", label: "Fitness / Movement" },
  { key: "massage_bodywork", label: "Massage / Bodywork" },
  { key: "nutrition_wellness", label: "Nutrition / Wellness" },
  { key: "longevity", label: "Longevity" },
  { key: "other", label: "Other" },
];

const emptyStep = () => ({ title: "", minutes: 5, product: "" });

export default function WellnessCreator() {
  const [spaName, setSpaName] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [timeOfDay, setTimeOfDay] = useState("evening");
  const [steps, setSteps] = useState([emptyStep()]);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);
  // Wellness Professional attribution + intention/why-useful fields.
  const [professionalType, setProfessionalType] = useState("spa");
  const [city, setCity] = useState("");
  const [intention, setIntention] = useState("");
  const [whyUseful, setWhyUseful] = useState("");

  const total = useMemo(() => steps.reduce((n, s) => n + (parseInt(s.minutes) || 0), 0), [steps]);

  const updateStep = (idx, patch) => setSteps((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  const addStep = () => setSteps((prev) => [...prev, emptyStep()]);
  const removeStep = (idx) => setSteps((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== idx)));

  const canSave = spaName.trim() && title.trim() && steps.some((s) => s.title.trim());

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const payload = {
        spa_name: spaName.trim(),
        title: title.trim(),
        description: description.trim(),
        time_of_day: timeOfDay,
        professional_type: professionalType,
        city: city.trim() || null,
        intention: intention.trim() || null,
        why_useful: whyUseful.trim() || null,
        steps: steps
          .filter((s) => s.title.trim())
          .map((s) => ({
            title: s.title.trim(),
            minutes: parseInt(s.minutes) || 5,
            product: (s.product || "").trim() || null,
          })),
      };
      const { data } = await endpoints.spaCreate(payload);
      setCreated(data);
      toast.success("Ritual published.");
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Could not publish. Check your inputs.");
    }
    setSaving(false);
  };

  const shareUrl = created ? `${window.location.origin}/r/${created.code}` : "";

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
      toast.success("Link copied.");
    } catch {
      toast.error("Copy blocked — select and copy manually.");
    }
  };

  const startAnother = () => {
    setCreated(null);
    setSpaName("");
    setTitle("");
    setDescription("");
    setSteps([emptyStep()]);
    setTimeOfDay("evening");
    setProfessionalType("spa");
    setCity("");
    setIntention("");
    setWhyUseful("");
  };

  return (
    <div className="space-y-10" data-testid="spa-creator-page">
      <header className="reveal">
        <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))] inline-flex items-center gap-2">
          <Flower2 size={12} className="text-[hsl(var(--terracotta))]" /> For wellness professionals
        </div>
        <h1 className="font-display text-5xl lg:text-6xl mt-3 tracking-tight italic">Share a Ritual</h1>
        <p className="mt-4 max-w-2xl text-[hsl(var(--muted-foreground))]">
          Craft a signature ritual you actually use with clients. Share it with a link or code. Members can add it to
          their own daily practice without losing anything of theirs.
        </p>
        <p className="mt-3 max-w-2xl text-[13px] italic text-[hsl(var(--muted-foreground))]">
          Share what works. Don't sell what you sell. A useful ritual belongs on N8. A sales pitch does not.
        </p>
      </header>

      {!created ? (
        <section className="p-8 border hairline rounded-md bg-[hsl(var(--paper))] reveal reveal-1" data-testid="spa-form">
          <div className="grid md:grid-cols-2 gap-6">
            <Field label="Business name">
              <Input value={spaName} onChange={(e) => setSpaName(e.target.value)} placeholder="Rose & Ember Wellness" data-testid="spa-name-input" className="bg-transparent" />
            </Field>
            <Field label="Ritual title">
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Post-lift decompression" data-testid="spa-title-input" className="bg-transparent" />
            </Field>
          </div>

          <div className="mt-6 grid md:grid-cols-2 gap-6">
            <Field label="Professional type">
              <select
                value={professionalType}
                onChange={(e) => setProfessionalType(e.target.value)}
                data-testid="spa-professional-type"
                className="w-full h-10 px-3 rounded-md bg-transparent border hairline focus:outline-none focus:ring-1 focus:ring-[hsl(var(--terracotta))]"
              >
                {PROFESSIONAL_TYPES.map((p) => (
                  <option key={p.key} value={p.key}>{p.label}</option>
                ))}
              </select>
            </Field>
            <Field label="City (optional)">
              <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Austin" data-testid="spa-city-input" className="bg-transparent" />
            </Field>
          </div>

          <div className="mt-6">
            <Field label="A short description">
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="A quiet 25 minutes for your skin and shoulders…"
                data-testid="spa-description-input"
                className="bg-transparent border-[hsl(var(--border))] focus-visible:ring-[hsl(var(--terracotta))] min-h-[80px]"
              />
            </Field>
          </div>

          <div className="mt-6 grid md:grid-cols-2 gap-6">
            <Field label="Intention / purpose (optional)">
              <Textarea
                value={intention}
                onChange={(e) => setIntention(e.target.value)}
                placeholder="Move from sympathetic dominance back to rest and digest."
                data-testid="spa-intention-input"
                className="bg-transparent border-[hsl(var(--border))] focus-visible:ring-[hsl(var(--terracotta))] min-h-[70px]"
                maxLength={280}
              />
            </Field>
            <Field label="Why you find it useful (optional)">
              <Textarea
                value={whyUseful}
                onChange={(e) => setWhyUseful(e.target.value)}
                placeholder="Clients recover faster between sessions and sleep deeper the same night."
                data-testid="spa-why-useful-input"
                className="bg-transparent border-[hsl(var(--border))] focus-visible:ring-[hsl(var(--terracotta))] min-h-[70px]"
                maxLength={280}
              />
            </Field>
          </div>

          <div className="mt-6">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))] mb-2">When</div>
            <div className="flex flex-wrap gap-2" data-testid="spa-time-options">
              {TIME_OPTIONS.map((t) => {
                const on = timeOfDay === t.key;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setTimeOfDay(t.key)}
                    data-testid={`spa-time-${t.key}`}
                    className={`px-4 py-1.5 rounded-full text-sm border hairline tab-link ${
                      on ? "bg-[hsl(var(--terracotta))] text-[hsl(var(--fg))] border-transparent" : "hover:bg-[hsl(var(--sand))]"
                    }`}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-8">
            <div className="flex items-baseline justify-between mb-3">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Steps</div>
              <div className="text-sm text-[hsl(var(--muted-foreground))]" data-testid="spa-total">
                Total <span className="font-display text-lg align-middle">{total}</span> min
              </div>
            </div>

            <ol className="space-y-3" data-testid="spa-steps-list">
              {steps.map((s, idx) => (
                <li key={idx} className="p-4 border hairline rounded-md bg-[hsl(var(--sand))]/30" data-testid={`spa-step-${idx}`}>
                  <div className="grid md:grid-cols-[1.4fr,90px,1.2fr,auto] gap-3 items-end">
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">Step {idx + 1}</span>
                      <Input
                        value={s.title}
                        onChange={(e) => updateStep(idx, { title: e.target.value })}
                        placeholder="Warm compress"
                        data-testid={`spa-step-title-${idx}`}
                        className="bg-transparent mt-1"
                      />
                    </label>
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">Min</span>
                      <Input
                        type="number"
                        min="1"
                        value={s.minutes}
                        onChange={(e) => updateStep(idx, { minutes: e.target.value })}
                        data-testid={`spa-step-minutes-${idx}`}
                        className="bg-transparent mt-1"
                      />
                    </label>
                    <label className="block">
                      <span className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">Product (optional)</span>
                      <Input
                        value={s.product}
                        onChange={(e) => updateStep(idx, { product: e.target.value })}
                        placeholder="Rosehip oil cleanser"
                        data-testid={`spa-step-product-${idx}`}
                        className="bg-transparent mt-1"
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => removeStep(idx)}
                      disabled={steps.length === 1}
                      data-testid={`spa-step-remove-${idx}`}
                      className="w-10 h-10 rounded-full grid place-items-center text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--destructive))] hover:bg-[hsl(var(--sand))] disabled:opacity-30"
                      aria-label={`Remove step ${idx + 1}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </li>
              ))}
            </ol>

            <div className="mt-4">
              <button
                type="button"
                onClick={addStep}
                data-testid="spa-add-step"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-dashed hairline text-sm hover:bg-[hsl(var(--sand))] tab-link"
              >
                <Plus size={14} /> Add a step
              </button>
            </div>
          </div>

          <div className="mt-8 flex justify-end">
            <Button
              onClick={save}
              disabled={!canSave || saving}
              data-testid="spa-publish"
              className="rounded-full bg-[hsl(var(--terracotta))] hover:bg-[hsl(var(--terracotta))]/90 text-[hsl(var(--fg))]"
            >
              {saving ? "Publishing…" : "Publish ritual"}
            </Button>
          </div>
        </section>
      ) : (
        <section className="p-8 border hairline rounded-md bg-[hsl(var(--paper))] reveal reveal-1" data-testid="spa-created-card">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))] inline-flex items-center gap-2">
            <Sparkles size={13} className="text-[hsl(var(--terracotta))]" /> Ready to share
          </div>
          <h2 className="font-display text-4xl mt-3 tracking-tight italic">{created.title}</h2>
          <p className="mt-1 text-[hsl(var(--muted-foreground))]">by {created.spa_name}</p>

          <div className="mt-6 grid md:grid-cols-[1fr,auto] gap-3 items-center">
            <div className="p-4 border hairline rounded-md bg-[hsl(var(--sand))]/40 font-mono text-sm break-all" data-testid="spa-share-url">
              {shareUrl}
            </div>
            <Button
              onClick={copyUrl}
              data-testid="spa-copy-url"
              className="rounded-full bg-[hsl(var(--clay))] hover:bg-[hsl(var(--clay))]/90 text-[hsl(var(--paper))]"
            >
              {copied ? <Check size={14} className="mr-2" /> : <Copy size={14} className="mr-2" />}
              {copied ? "Copied" : "Copy link"}
            </Button>
          </div>

          <div className="mt-4 text-sm text-[hsl(var(--muted-foreground))]">
            Or share the code:{" "}
            <span className="font-display text-2xl tracking-widest text-[hsl(var(--fg))] ml-1" data-testid="spa-share-code">
              {created.code}
            </span>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to={`/r/${created.code}`}
              data-testid="spa-view-live"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border hairline text-sm hover:bg-[hsl(var(--sand))] tab-link"
            >
              Preview client view <ArrowUpRight size={14} />
            </Link>
            <Button onClick={startAnother} variant="outline" data-testid="spa-create-another" className="rounded-full">
              Create another
            </Button>
          </div>
        </section>
      )}

      <SendClientPhotosCard />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

export function SendClientPhotosCard() {
  const [clientCode, setClientCode] = useState("");
  const [procedureName, setProcedureName] = useState("");
  const [spaName, setSpaName] = useState("");
  const [note, setNote] = useState("");
  const [beforeFile, setBeforeFile] = useState(null);
  const [afterFile, setAfterFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sentHistory, setSentHistory] = useState([]);
  // Consent status for the current (spa_user, client_code) pair. null when
  // not yet checked. { status: "active"|"pending"|"denied"|"revoked"|"unknown", id?, spa_name? }
  const [consent, setConsent] = useState(null);
  const [checkingConsent, setCheckingConsent] = useState(false);
  const [requestingConsent, setRequestingConsent] = useState(false);

  const beforePreview = useMemo(() => (beforeFile ? URL.createObjectURL(beforeFile) : null), [beforeFile]);
  const afterPreview = useMemo(() => (afterFile ? URL.createObjectURL(afterFile) : null), [afterFile]);

  const codeReady = clientCode.trim().length >= 4;
  const nameReady = spaName.trim().length > 0;
  const consentActive = consent?.status === "active";
  const canSend = codeReady && procedureName.trim() && nameReady && beforeFile && afterFile && consentActive;

  // Look up existing consent whenever the code changes and a wellness_name is set.
  const refreshConsent = async () => {
    if (!codeReady || !nameReady) { setConsent(null); return; }
    setCheckingConsent(true);
    try {
      const { data } = await endpoints.listSpaConsents();
      const match = (data || []).find((c) => (c.client_code_snapshot || "").toUpperCase() === clientCode.trim().toUpperCase());
      setConsent(match || { status: "unknown" });
    } catch {
      setConsent({ status: "unknown" });
    }
    setCheckingConsent(false);
  };

  useEffect(() => {
    const t = setTimeout(refreshConsent, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientCode, spaName]);

  const requestAccess = async () => {
    if (!codeReady || !nameReady) return;
    setRequestingConsent(true);
    try {
      const { data } = await endpoints.requestSpaConsent({
        client_code: clientCode.trim().toUpperCase(),
        spa_name: spaName.trim(),
        note: note.trim() || `Request from ${spaName.trim()} to send Before/After photos.`,
      });
      setConsent(data);
      if (data.status === "active") {
        toast.success("Consent is already active — you can send now.");
      } else {
        toast.success("Request sent. The client will see it in their notifications.");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Could not request access.");
    }
    setRequestingConsent(false);
  };

  const loadHistory = async (name) => {
    const n = (name || spaName || "").trim();
    if (!n) { setSentHistory([]); return; }
    try {
      const { data } = await endpoints.spaSentPhotos(n);
      setSentHistory(data);
    } catch {
      setSentHistory([]);
    }
  };

  // Reload history whenever the wellness name changes (debounced) and then every 15s.
  useEffect(() => {
    const t = setTimeout(() => loadHistory(spaName), 300);
    return () => clearTimeout(t);
  }, [spaName]);
  useEffect(() => {
    if (!spaName.trim()) return;
    const t = setInterval(() => loadHistory(spaName), 15000);
    return () => clearInterval(t);
  }, [spaName]);

  const submit = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("client_code", clientCode.trim().toUpperCase());
      fd.append("procedure_name", procedureName.trim());
      fd.append("spa_name", spaName.trim());
      fd.append("note", note.trim());
      fd.append("before", beforeFile);
      fd.append("after", afterFile);
      await endpoints.sendProcedurePhotos(fd);
      setSent(true);
      toast.success("Photos sent to your client.");
      setTimeout(() => setSent(false), 3000);
      setClientCode("");
      setProcedureName("");
      setNote("");
      setBeforeFile(null);
      setAfterFile(null);
      setConsent(null);
      loadHistory(spaName);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "consent_required") {
        toast.error("Consent required. A request has been sent to the client.");
        await refreshConsent();
      } else {
        const msg = typeof detail === "string" ? detail : "Could not send. Check the client code and try again.";
        toast.error(msg);
      }
    }
    setSending(false);
  };

  return (
    <section className="p-8 border hairline rounded-md bg-[hsl(var(--paper))] reveal reveal-2" data-testid="spa-send-photos">
      <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))] inline-flex items-center gap-2">
        <ImageUp size={12} className="text-[hsl(var(--terracotta))]" /> Becoming photos — then &amp; now
      </div>
      <h2 className="font-display text-4xl mt-3 tracking-tight italic">Send a client their photos</h2>
      <p className="mt-2 max-w-2xl text-sm text-[hsl(var(--muted-foreground))]">
        Ask the client for their 8-character code (found on their Profile). Every send is gated by the client's consent —
        they hold the key, always. Once they accept your request, the pair lands in their Tracking tab with a chime.
      </p>

      <div className="mt-6 grid md:grid-cols-2 gap-6">
        <Field label="Wellness practice name">
          <Input value={spaName} onChange={(e) => setSpaName(e.target.value)} placeholder="Rose & Ember Wellness" data-testid="send-wellness-name" className="bg-transparent" />
        </Field>
        <Field label="Client code (from their Profile)">
          <Input
            value={clientCode}
            onChange={(e) => setClientCode(e.target.value.toUpperCase())}
            placeholder="e.g. A1B2C3D4"
            data-testid="send-client-code"
            maxLength={12}
            className="bg-transparent font-mono tracking-widest"
          />
        </Field>
        <Field label="Procedure name">
          <Input value={procedureName} onChange={(e) => setProcedureName(e.target.value)} placeholder="Microneedling · session 3" data-testid="send-procedure-name" className="bg-transparent" />
        </Field>
        <Field label="Note (optional)">
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Two weeks post-treatment." data-testid="send-note" className="bg-transparent" />
        </Field>
      </div>

      <ConsentBanner
        codeReady={codeReady}
        nameReady={nameReady}
        consent={consent}
        checking={checkingConsent}
        requesting={requestingConsent}
        onRequest={requestAccess}
      />

      <div className="mt-6 grid sm:grid-cols-2 gap-4" data-testid="send-uploads">
        <PhotoDrop
          label="Then"
          file={beforeFile}
          preview={beforePreview}
          onChange={setBeforeFile}
          testid="send-before-input"
        />
        <PhotoDrop
          label="Now"
          file={afterFile}
          preview={afterPreview}
          onChange={setAfterFile}
          testid="send-after-input"
        />
      </div>

      <div className="mt-6 flex justify-end">
        <Button
          onClick={submit}
          disabled={!canSend || sending}
          data-testid="send-photos-submit"
          className="rounded-full bg-[hsl(var(--terracotta))] hover:bg-[hsl(var(--terracotta))]/90 text-[hsl(var(--fg))]"
        >
          {sending ? "Sending…" : sent ? (<><Check size={14} className="mr-2" /> Sent</>) : (<><Send size={14} className="mr-2" /> Send to client</>)}
        </Button>
      </div>

      {spaName.trim() && (
        <div className="mt-8 pt-6 border-t hairline" data-testid="sent-history">
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))] inline-flex items-center gap-2">
              <History size={12} /> Recently sent as {spaName.trim()}
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]" data-testid="sent-history-count">
              {sentHistory.length} sent
            </div>
          </div>
          {sentHistory.length === 0 ? (
            <p className="mt-4 text-sm text-[hsl(var(--muted-foreground))]" data-testid="sent-history-empty">
              Nothing yet. Your sends will appear here with your client's reactions.
            </p>
          ) : (
            <ul className="mt-4 divide-y hairline" data-testid="sent-history-list">
              {sentHistory.map((p) => {
                const c = p.reaction_counts || { love: 0, wow: 0 };
                return (
                  <li key={p.id} className="py-3 flex items-center justify-between gap-4 flex-wrap" data-testid={`sent-item-${p.id}`}>
                    <div className="min-w-0">
                      <div className="font-display text-lg leading-tight truncate">{p.procedure_name}</div>
                      <div className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mt-1">
                        {new Date(p.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                        {" · "}client {p.client_code}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-xs" data-testid={`sent-reactions-${p.id}`}>
                      <span className={`inline-flex items-center gap-1 ${c.love > 0 ? "text-[hsl(var(--terracotta))]" : "text-[hsl(var(--muted-foreground))]"}`} title={c.love > 0 ? "Client loved this" : "No love yet"}>
                        <Heart size={12} fill={c.love > 0 ? "currentColor" : "none"} /> {c.love}
                      </span>
                      <span className={`inline-flex items-center gap-1 ${c.wow > 0 ? "text-[hsl(var(--clay))]" : "text-[hsl(var(--muted-foreground))]"}`} title={c.wow > 0 ? "Client said wow" : "No wow yet"}>
                        <Zap size={12} fill={c.wow > 0 ? "currentColor" : "none"} /> {c.wow}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

function ConsentBanner({ codeReady, nameReady, consent, checking, requesting, onRequest }) {
  if (!codeReady || !nameReady) return null;
  const status = consent?.status || (checking ? "checking" : "unknown");

  const wrap = (border, tone, icon, label, body, action = null) => (
    <div
      className={`mt-6 p-4 border rounded-md ${border} ${tone} flex items-start gap-3`}
      data-testid={`consent-banner-${status}`}
    >
      <span className="mt-0.5">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] uppercase tracking-[0.22em] font-medium">{label}</div>
        <p className="mt-1 text-sm leading-relaxed">{body}</p>
      </div>
      {action}
    </div>
  );

  if (status === "checking") {
    return wrap("hairline", "bg-[hsl(var(--sand))]/30 text-[hsl(var(--muted-foreground))]", <Clock size={16} />, "Checking access", "One second — looking up whether the client has authorized this wellness practice yet.");
  }
  if (status === "active") {
    return wrap("border-[hsl(var(--moss))]/40", "bg-[hsl(var(--moss))]/10 text-[hsl(var(--fg))]", <ShieldCheck size={16} className="text-[hsl(var(--moss))]" />, "Consent active", "The client has authorized this wellness practice to send Before/After photos. You're clear to upload and send.");
  }
  if (status === "pending") {
    return wrap("border-[hsl(var(--clay))]/40", "bg-[hsl(var(--clay))]/10 text-[hsl(var(--fg))]", <Clock size={16} className="text-[hsl(var(--clay))]" />, "Waiting on the client", "Your request has been sent — the client will see it in their notifications and can accept from their Profile. You'll be able to send photos as soon as they do.");
  }
  if (status === "denied") {
    return wrap("border-[hsl(var(--destructive))]/30", "bg-[hsl(var(--destructive))]/5 text-[hsl(var(--fg))]", <ShieldOff size={16} className="text-[hsl(var(--destructive))]" />, "Access declined", "The client has declined this request. Please reach out to them directly if you need to revisit this.");
  }
  if (status === "revoked") {
    return wrap("border-[hsl(var(--destructive))]/30", "bg-[hsl(var(--destructive))]/5 text-[hsl(var(--fg))]", <ShieldAlert size={16} className="text-[hsl(var(--destructive))]" />, "Access revoked", "The client revoked access. You'll need to ask them again to send new photos.",
      <Button size="sm" variant="outline" data-testid="consent-request-btn" disabled={requesting} onClick={onRequest} className="rounded-full">
        {requesting ? "Sending…" : "Request access again"}
      </Button>
    );
  }
  // unknown → invite the wellness practice to send a request first.
  return wrap("hairline", "bg-[hsl(var(--sand))]/30 text-[hsl(var(--muted-foreground))]", <ShieldAlert size={16} />, "Consent needed", "This client hasn't authorized this wellness practice yet. Send a soft request — they'll accept from their Profile, and then you can share photos.",
    <Button size="sm" data-testid="consent-request-btn" disabled={requesting} onClick={onRequest} className="rounded-full bg-[hsl(var(--terracotta))] hover:bg-[hsl(var(--terracotta))]/90 text-[hsl(var(--fg))]">
      {requesting ? "Sending…" : "Request access"}
    </Button>
  );
}

function PhotoDrop({ label, file, preview, onChange, testid }) {
  const inputId = `photo-drop-${label.toLowerCase()}`;
  return (
    <div className="p-4 border hairline rounded-md bg-[hsl(var(--sand))]/30">
      <div className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">{label}</div>
      <label htmlFor={inputId} className="mt-3 block cursor-pointer">
        <div className="aspect-[4/3] rounded-md border border-dashed hairline grid place-items-center overflow-hidden bg-[hsl(var(--paper))]">
          {preview ? (
            <img src={preview} alt={`${label} preview`} className="w-full h-full object-cover" />
          ) : (
            <div className="text-center text-xs text-[hsl(var(--muted-foreground))] px-4">
              <ImageUp size={20} className="mx-auto mb-2 opacity-60" />
              Tap to choose a {label.toLowerCase()} photo
            </div>
          )}
        </div>
      </label>
      <input
        id={inputId}
        type="file"
        accept="image/*"
        data-testid={testid}
        className="hidden"
        onChange={(e) => onChange(e.target.files?.[0] || null)}
      />
      {file && (
        <div className="mt-2 flex items-center justify-between text-xs text-[hsl(var(--muted-foreground))]">
          <span className="truncate max-w-[75%]">{file.name}</span>
          <button
            type="button"
            onClick={() => onChange(null)}
            className="hover:text-[hsl(var(--destructive))]"
            aria-label={`Clear ${label} photo`}
          >
            <Trash2 size={12} />
          </button>
        </div>
      )}
    </div>
  );
}
